import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, CircularProgress, Alert, AlertTitle,
  Button, Tooltip, Divider, LinearProgress, IconButton, Collapse, Switch,
  FormControlLabel, alpha, useTheme,
} from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RefreshIcon from '@mui/icons-material/Refresh';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StorageIcon from '@mui/icons-material/Storage';
import TuneIcon from '@mui/icons-material/Tune';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import SecurityIcon from '@mui/icons-material/Security';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';

const STRATEGY_ENGINE_URL =
  process.env.REACT_APP_STRATEGY_ENGINE_URL ||
  'https://strategy-engine.houman-ghavamzadeh.workers.dev';

const headers = () => ({
  'X-User-Id': localStorage.getItem('user_id') || 'default-user',
  'Content-Type': 'application/json',
});

// ============================================================================
// STATUS CARD — Experience Mode overview
// ============================================================================
function StatusCard({ status, onAdvance, advancing }) {
  const theme = useTheme();
  if (!status) return null;

  const { data } = status;
  const combo = data.combination || {};
  const settings = data.settings || {};
  const progress = combo.totalCombinations > 0
    ? ((combo.currentIndex + 1) / combo.totalCombinations) * 100
    : 0;

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: data.experienceModeEnabled ? 'primary.main' : 'divider', borderRadius: 2, bgcolor: data.experienceModeEnabled ? alpha(theme.palette.primary.main, 0.02) : 'background.paper' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScienceIcon sx={{ fontSize: 20, color: data.experienceModeEnabled ? 'primary.main' : 'text.disabled' }} />
          <Typography variant="subtitle2" fontWeight="bold">Experience Mode</Typography>
          <Chip
            label={data.experienceModeEnabled ? 'Active' : 'Off'}
            size="small"
            color={data.experienceModeEnabled ? 'primary' : 'default'}
            variant={data.experienceModeEnabled ? 'filled' : 'outlined'}
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
        </Box>
        {data.experienceModeEnabled && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip
              icon={data.tradingAllowed ? <CheckCircleIcon /> : <BlockIcon />}
              label={data.tradingAllowed ? 'Trading Day' : data.tradingBlockedReason}
              size="small"
              color={data.tradingAllowed ? 'success' : 'error'}
              variant="outlined"
              sx={{ fontSize: '0.65rem', height: 22 }}
            />
            {data.isHoldout && (
              <Chip label="Holdout (Control)" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.65rem', height: 22 }} />
            )}
          </Box>
        )}
      </Box>

      {data.experienceModeEnabled && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Combination</Typography>
              <Typography variant="body2" fontWeight={600}>
                #{combo.currentIndex} / {combo.totalCombinations}
              </Typography>
              <Typography variant="caption" color="text.secondary">{combo.currentLabel}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Workdays</Typography>
              <Typography variant="body2" fontWeight={600}>{combo.workdays}</Typography>
              <Typography variant="caption" color="text.secondary">
                min {settings.minDaysPerCombo} / max {settings.maxDaysPerCombo}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Trades Collected</Typography>
              <Typography variant="body2" fontWeight={600}>{combo.trades}</Typography>
              <Typography variant="caption" color="text.secondary">
                need {settings.minTradesForDecision}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Auto Apply</Typography>
              <Typography variant="body2" fontWeight={600} color={settings.autoApply ? 'success.main' : 'text.secondary'}>
                {settings.autoApply ? 'ON' : 'OFF'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                max delta: {(settings.autoApplyMaxDelta * 100).toFixed(0)}%
              </Typography>
            </Grid>
          </Grid>

          <Box sx={{ mt: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Cycle Progress</Typography>
              <Typography variant="caption" fontWeight={600}>{progress.toFixed(0)}%</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ height: 6, borderRadius: 3, bgcolor: alpha(theme.palette.primary.main, 0.1) }}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5, gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<SkipNextIcon />}
              onClick={onAdvance}
              disabled={advancing}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              {advancing ? 'Advancing...' : 'Advance to Next'}
            </Button>
          </Box>
        </>
      )}

      {!data.experienceModeEnabled && (
        <Typography variant="caption" color="text.secondary">
          Enable Experience Mode in Settings to start ML-driven optimization experiments.
        </Typography>
      )}
    </Paper>
  );
}

// ============================================================================
// BEST MODE PANEL — Winners by metric and regime
// ============================================================================
function BestModePanel({ bestMode }) {
  const theme = useTheme();
  if (!bestMode) return null;

  const { overall, byRegime, byObjective } = bestMode;

  const renderBest = (label, result, color) => {
    if (!result) return (
      <Box sx={{ p: 1, bgcolor: alpha(theme.palette.text.disabled, 0.05), borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body2" color="text.disabled">Insufficient data</Typography>
      </Box>
    );
    return (
      <Box sx={{ p: 1, bgcolor: alpha(theme.palette[color]?.main || theme.palette.primary.main, 0.05), borderRadius: 1, border: '1px solid', borderColor: alpha(theme.palette[color]?.main || theme.palette.primary.main, 0.15) }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body2" fontWeight={600}>
          Combo #{result.combinationIndex} — {result.combinationLabel}
        </Typography>
        <Typography variant="caption" fontWeight={600} color={`${color}.main`}>
          {result.metric === 'net_pnl' ? `${result.value > 0 ? '+' : ''}${result.value.toFixed(2)}%` :
           result.metric === 'win_rate' ? `${(result.value * 100).toFixed(1)}% WR` :
           `${result.value.toFixed(2)}% DD`}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
          ({result.tradeCount} trades, {(result.confidence * 100).toFixed(0)}% conf)
        </Typography>
      </Box>
    );
  };

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <EmojiEventsIcon sx={{ fontSize: 18, color: 'warning.main' }} />
        <Typography variant="subtitle2" fontWeight="bold">Best Mode Report</Typography>
      </Box>

      <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Overall Winners
      </Typography>
      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>{renderBest('Best Net PnL', overall.bestNetPnl, 'success')}</Grid>
        <Grid item xs={12} sm={4}>{renderBest('Best Win Rate', overall.bestWinRate, 'info')}</Grid>
        <Grid item xs={12} sm={4}>{renderBest('Min Drawdown', overall.minDrawdown, 'warning')}</Grid>
      </Grid>

      <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        By Market Regime
      </Typography>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" color="text.secondary">Ranging</Typography>
          {renderBest('PnL', byRegime.ranging?.bestNetPnl, 'success')}
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" color="text.secondary">Weak Trend</Typography>
          {renderBest('PnL', byRegime.weakTrend?.bestNetPnl, 'success')}
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" color="text.secondary">Strong Trend</Typography>
          {renderBest('PnL', byRegime.strongTrend?.bestNetPnl, 'success')}
        </Grid>
      </Grid>
    </Paper>
  );
}

// ============================================================================
// SUGGESTION PANEL — ML recommendation with diff + apply
// ============================================================================
function SuggestionPanel({ strategyId, suggestion, onGenerate, onApply, generating, applying }) {
  const theme = useTheme();
  const [showDiff, setShowDiff] = useState(false);

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoFixHighIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
          <Typography variant="subtitle2" fontWeight="bold">ML Suggestion</Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<LightbulbIcon />}
          onClick={onGenerate}
          disabled={generating}
          sx={{ textTransform: 'none', fontSize: '0.75rem' }}
        >
          {generating ? 'Generating...' : 'Generate Suggestion'}
        </Button>
      </Box>

      {suggestion ? (
        <>
          <Alert
            severity={suggestion.confidence > 0.5 ? 'success' : suggestion.confidence > 0.2 ? 'info' : 'warning'}
            variant="outlined"
            sx={{ mb: 1.5, '& .MuiAlert-message': { fontSize: '0.8rem' } }}
          >
            <AlertTitle sx={{ fontSize: '0.85rem' }}>{suggestion.why}</AlertTitle>
            Confidence: {(suggestion.confidence * 100).toFixed(0)}% | Regime: {suggestion.regimeContext?.currentRegime} (ADX: {suggestion.regimeContext?.adx})
          </Alert>

          {suggestion.diffSummary && suggestion.diffSummary.length > 0 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography
                  variant="caption"
                  sx={{ cursor: 'pointer', color: 'primary.main' }}
                  onClick={() => setShowDiff(!showDiff)}
                >
                  {showDiff ? 'Hide' : 'Show'} Changes ({suggestion.diffSummary.length})
                  {showDiff ? <ExpandLessIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} /> : <ExpandMoreIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />}
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() => onApply(suggestion.recommendedPatch)}
                  disabled={applying}
                  startIcon={<PlayArrowIcon />}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  {applying ? 'Applying...' : 'Apply Patch'}
                </Button>
              </Box>
              <Collapse in={showDiff}>
                <Box sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
                  {suggestion.diffSummary.map((d, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" fontWeight={600} sx={{ minWidth: 180 }}>{d.field}</Typography>
                      <Chip label={String(d.from)} size="small" sx={{ fontSize: '0.65rem', height: 18, bgcolor: alpha(theme.palette.error.main, 0.08) }} />
                      <Typography variant="caption">→</Typography>
                      <Chip label={String(d.to)} size="small" sx={{ fontSize: '0.65rem', height: 18, bgcolor: alpha(theme.palette.success.main, 0.08) }} />
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </>
          )}
        </>
      ) : (
        <Typography variant="caption" color="text.secondary">
          Click "Generate Suggestion" to get ML-driven recommendations based on experiment history.
        </Typography>
      )}
    </Paper>
  );
}

// ============================================================================
// WINDOWS PANEL — Intraday collection windows
// ============================================================================
function WindowsPanel({ windows }) {
  const theme = useTheme();
  if (!windows) return null;

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <ScheduleIcon sx={{ fontSize: 18, color: 'info.main' }} />
        <Typography variant="subtitle2" fontWeight="bold">Data Collection Windows</Typography>
        <Chip label={`${windows.tradeCount || 0} trades analyzed`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        {(windows.recommendedWindowsLocal || []).map((w, i) => (
          <Chip
            key={i}
            label={w}
            color="primary"
            variant="outlined"
            size="small"
            sx={{ fontWeight: 600 }}
          />
        ))}
      </Box>

      <Typography variant="caption" color="text.secondary">
        Confidence: {((windows.confidence || 0) * 100).toFixed(0)}% — {windows.justification || 'Using defaults'}
      </Typography>

      {windows.hourScores && windows.hourScores.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Hour Scores (Top 5)
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {windows.hourScores
              .filter(h => h.score > 0)
              .sort((a, b) => b.score - a.score)
              .slice(0, 5)
              .map((h, i) => (
                <Chip
                  key={i}
                  label={`${h.hour}:00 (${h.score.toFixed(1)})`}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.65rem', height: 20,
                    borderColor: h.score > 20 ? 'success.main' : h.score > 10 ? 'info.main' : 'text.disabled',
                  }}
                />
              ))}
          </Box>
        </Box>
      )}
    </Paper>
  );
}

// ============================================================================
// LEDGER PANEL — Recent experiment actions
// ============================================================================
function LedgerPanel({ ledger }) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  if (!ledger || ledger.length === 0) return null;

  const actionColors = {
    combination_applied: 'primary',
    suggestion_generated: 'info',
    suggestion_applied: 'success',
    auto_apply: 'warning',
    manual_override: 'secondary',
    combination_advanced: 'default',
  };

  const recent = expanded ? ledger.slice(0, 20) : ledger.slice(0, 5);

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CompareArrowsIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="subtitle2" fontWeight="bold">Experiment Ledger</Typography>
          <Chip label={`${ledger.length} entries`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
        </Box>
        <IconButton size="small" onClick={() => setExpanded(!expanded)}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      {recent.map((entry, i) => (
        <Box
          key={i}
          sx={{
            display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.75,
            borderBottom: i < recent.length - 1 ? '1px solid' : 'none', borderColor: 'divider',
          }}
        >
          <Chip
            label={entry.action?.replace(/_/g, ' ')}
            size="small"
            color={actionColors[entry.action] || 'default'}
            variant="outlined"
            sx={{ fontSize: '0.6rem', height: 18, minWidth: 100 }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ display: 'block' }}>
              Combo #{entry.combinationIndex} — {entry.combinationLabel}
            </Typography>
            {entry.notes && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                {entry.notes}
              </Typography>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {entry.timestamp ? new Date(entry.timestamp).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
          </Typography>
        </Box>
      ))}
    </Paper>
  );
}

// ============================================================================
// PARAMETER SPACE PANEL — ML-experimentable parameters by category
// ============================================================================
const categoryIcons = {
  risk: <SecurityIcon sx={{ fontSize: 14 }} />,
  rescue: <HealthAndSafetyIcon sx={{ fontSize: 14 }} />,
  exit: <TrendingUpIcon sx={{ fontSize: 14 }} />,
  entry: <PlayArrowIcon sx={{ fontSize: 14 }} />,
  gate: <BlockIcon sx={{ fontSize: 14 }} />,
  timing: <ScheduleIcon sx={{ fontSize: 14 }} />,
  mode: <TuneIcon sx={{ fontSize: 14 }} />,
};

const categoryColors = {
  risk: 'error', rescue: 'warning', exit: 'success', entry: 'primary',
  gate: 'info', timing: 'secondary', mode: 'default',
};

function ParameterSpacePanel({ paramSpace, onInit, initializing }) {
  const theme = useTheme();
  const [expandedCat, setExpandedCat] = useState(null);

  if (!paramSpace) return null;

  const { paramsByCategory, activeParams, totalDefinedParams, storedParams } = paramSpace;
  const needsInit = storedParams === 0 && totalDefinedParams > 0;

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TuneIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          <Typography variant="subtitle2" fontWeight="bold">ML Parameter Space</Typography>
          <Chip label={`${totalDefinedParams} params`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
        </Box>
        {needsInit && (
          <Button size="small" variant="contained" onClick={onInit} disabled={initializing} sx={{ textTransform: 'none', fontSize: '0.7rem' }}>
            {initializing ? 'Initializing...' : 'Initialize'}
          </Button>
        )}
      </Box>

      {Object.entries(paramsByCategory || {}).map(([cat, params]) => (
        <Box key={cat} sx={{ mb: 0.5 }}>
          <Box
            onClick={() => setExpandedCat(expandedCat === cat ? null : cat)}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.5, cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) }, borderRadius: 1, px: 0.5 }}
          >
            {categoryIcons[cat] || <TuneIcon sx={{ fontSize: 14 }} />}
            <Typography variant="caption" fontWeight={600} sx={{ textTransform: 'capitalize', flex: 1 }}>{cat}</Typography>
            <Chip label={params.length} size="small" color={categoryColors[cat] || 'default'} variant="outlined" sx={{ fontSize: '0.6rem', height: 16, minWidth: 20 }} />
            {expandedCat === cat ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
          </Box>
          <Collapse in={expandedCat === cat}>
            <Box sx={{ pl: 2.5, pb: 1 }}>
              {params.map((p) => {
                const active = activeParams?.find(a => a.param_key === p.key);
                return (
                  <Box key={p.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.3 }}>
                    <Typography variant="caption" sx={{ flex: 1, minWidth: 0 }} noWrap>{p.label}</Typography>
                    {p.type === 'number' && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                        [{p.min}–{p.max}] step {p.step}
                      </Typography>
                    )}
                    {p.type === 'boolean' && (
                      <Chip label="ON/OFF" size="small" variant="outlined" sx={{ fontSize: '0.55rem', height: 14 }} />
                    )}
                    {p.type === 'enum' && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                        {(p.enumValues || []).join(' | ')}
                      </Typography>
                    )}
                    <Chip
                      label={active ? active.current_value : String(p.defaultValue)}
                      size="small"
                      color={active ? 'primary' : 'default'}
                      variant={active ? 'filled' : 'outlined'}
                      sx={{ fontSize: '0.6rem', height: 16, minWidth: 32 }}
                    />
                  </Box>
                );
              })}
            </Box>
          </Collapse>
        </Box>
      ))}
    </Paper>
  );
}

// ============================================================================
// DATA COLLECTION STATS — ML training data completeness
// ============================================================================
function DataCollectionPanel({ dataStats }) {
  const theme = useTheme();
  if (!dataStats) return null;

  const { stats, recentSamples } = dataStats;
  const completeness = stats.totalSamples > 0
    ? ((stats.withFullSettings / stats.totalSamples) * 100).toFixed(0)
    : 0;

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <StorageIcon sx={{ fontSize: 18, color: 'info.main' }} />
        <Typography variant="subtitle2" fontWeight="bold">ML Data Collection</Typography>
        <Chip label={`${completeness}% complete`} size="small" color={completeness >= 80 ? 'success' : completeness >= 50 ? 'warning' : 'error'} variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
      </Box>

      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={6} sm={3}>
          <Box sx={{ p: 1, bgcolor: alpha(theme.palette.primary.main, 0.04), borderRadius: 1, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700} color="primary.main">{stats.totalSamples}</Typography>
            <Typography variant="caption" color="text.secondary">Total Samples</Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ p: 1, bgcolor: alpha(theme.palette.success.main, 0.04), borderRadius: 1, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700} color="success.main">{stats.closedTrades}</Typography>
            <Typography variant="caption" color="text.secondary">Closed Trades</Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ p: 1, bgcolor: alpha(theme.palette.info.main, 0.04), borderRadius: 1, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700} color="info.main">{stats.withFullSettings}</Typography>
            <Typography variant="caption" color="text.secondary">With Settings</Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ p: 1, bgcolor: alpha(theme.palette.warning.main, 0.04), borderRadius: 1, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700} color="warning.main">{stats.withMarketContext}</Typography>
            <Typography variant="caption" color="text.secondary">With Context</Typography>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
        {(stats.uniqueModes || []).map(m => (
          <Chip key={m} label={m} size="small" variant="outlined" color="primary" sx={{ fontSize: '0.6rem', height: 18 }} />
        ))}
        {(stats.uniqueRegimes || []).map(r => (
          <Chip key={r} label={r} size="small" variant="outlined" color="secondary" sx={{ fontSize: '0.6rem', height: 18 }} />
        ))}
      </Box>

      {recentSamples && recentSamples.length > 0 && (
        <Box sx={{ maxHeight: 160, overflow: 'auto' }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Recent Samples</Typography>
          {recentSamples.slice(0, 5).map((s, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.3, borderBottom: '1px solid', borderColor: alpha(theme.palette.divider, 0.3), flexWrap: 'wrap' }}>
              <Typography variant="caption" fontWeight={600} sx={{ minWidth: 60 }}>{s.symbol}</Typography>
              <Chip label={s.side} size="small" color={s.side === 'buy' ? 'success' : 'error'} variant="outlined" sx={{ fontSize: '0.55rem', height: 14 }} />
              <Chip label={`${s.leverage}x`} size="small" variant="outlined" sx={{ fontSize: '0.55rem', height: 14 }} />
              {s.label_final_pnl_pct != null && (
                <Typography variant="caption" color={s.label_final_pnl_pct >= 0 ? 'success.main' : 'error.main'} fontWeight={600}>
                  {s.label_final_pnl_pct >= 0 ? '+' : ''}{Number(s.label_final_pnl_pct).toFixed(2)}%
                </Typography>
              )}
              {s.has_settings ? <DataUsageIcon sx={{ fontSize: 10, color: 'success.main' }} /> : <DataUsageIcon sx={{ fontSize: 10, color: 'text.disabled' }} />}
              {s.has_context ? <StorageIcon sx={{ fontSize: 10, color: 'success.main' }} /> : <StorageIcon sx={{ fontSize: 10, color: 'text.disabled' }} />}
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
}

// ============================================================================
// FUNNEL / GATE BLOCKERS PANEL — §8.1 requirement
// ============================================================================
function FunnelBlockersPanel({ snapshots }) {
  const theme = useTheme();
  if (!snapshots || snapshots.length === 0) return null;

  // Use latest 7d snapshot for funnel data
  const snap = snapshots
    .filter(s => s.period === '7d')
    .sort((a, b) => new Date(b.generatedAt) - new Date(a.generatedAt))[0];
  if (!snap || !snap.funnel) return null;

  const { signalsGenerated, signalsQualified, entriesExecuted, topBlockers } = snap.funnel;
  const passRate = signalsGenerated > 0 ? ((entriesExecuted / signalsGenerated) * 100).toFixed(1) : 0;

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <BlockIcon sx={{ fontSize: 18, color: 'error.main' }} />
        <Typography variant="subtitle2" fontWeight="bold">Funnel & Gate Blockers</Typography>
        <Chip label={`${passRate}% pass rate`} size="small" color={passRate > 20 ? 'success' : 'warning'} variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
      </Box>

      <Grid container spacing={1} sx={{ mb: 1.5 }}>
        <Grid item xs={4}>
          <Box sx={{ textAlign: 'center', p: 0.5, bgcolor: alpha(theme.palette.info.main, 0.04), borderRadius: 1 }}>
            <Typography variant="h6" fontWeight={700} color="info.main">{signalsGenerated}</Typography>
            <Typography variant="caption" color="text.secondary">Signals</Typography>
          </Box>
        </Grid>
        <Grid item xs={4}>
          <Box sx={{ textAlign: 'center', p: 0.5, bgcolor: alpha(theme.palette.warning.main, 0.04), borderRadius: 1 }}>
            <Typography variant="h6" fontWeight={700} color="warning.main">{signalsQualified}</Typography>
            <Typography variant="caption" color="text.secondary">Qualified</Typography>
          </Box>
        </Grid>
        <Grid item xs={4}>
          <Box sx={{ textAlign: 'center', p: 0.5, bgcolor: alpha(theme.palette.success.main, 0.04), borderRadius: 1 }}>
            <Typography variant="h6" fontWeight={700} color="success.main">{entriesExecuted}</Typography>
            <Typography variant="caption" color="text.secondary">Executed</Typography>
          </Box>
        </Grid>
      </Grid>

      {topBlockers && topBlockers.length > 0 && (
        <>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Top Blockers
          </Typography>
          {topBlockers.slice(0, 8).map((b, i) => {
            const maxCount = topBlockers[0]?.count || 1;
            return (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.3 }}>
                <Typography variant="caption" sx={{ minWidth: 140, fontWeight: 500 }} noWrap>{b.gate}</Typography>
                <Box sx={{ flex: 1, height: 6, bgcolor: alpha(theme.palette.error.main, 0.08), borderRadius: 3 }}>
                  <Box sx={{ width: `${(b.count / maxCount) * 100}%`, height: '100%', bgcolor: 'error.main', borderRadius: 3 }} />
                </Box>
                <Typography variant="caption" fontWeight={600} sx={{ minWidth: 28, textAlign: 'right' }}>{b.count}</Typography>
              </Box>
            );
          })}
        </>
      )}

      {(!topBlockers || topBlockers.length === 0) && (
        <Typography variant="caption" color="text.secondary">No blocker data available yet.</Typography>
      )}
    </Paper>
  );
}

// ============================================================================
// LOSS PATTERNS PANEL — §8.1 requirement
// ============================================================================
function LossPatternsPanel({ snapshots }) {
  const theme = useTheme();
  if (!snapshots || snapshots.length === 0) return null;

  const snap = snapshots
    .filter(s => s.period === '7d')
    .sort((a, b) => new Date(b.generatedAt) - new Date(a.generatedAt))[0];
  if (!snap) return null;

  const lp = snap.lossPatterns || {};
  const er = snap.exitReasons || {};
  const totalTrades = snap.tradeCount || 1;

  const exitEntries = Object.entries(er).sort((a, b) => b[1] - a[1]);

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <WarningAmberIcon sx={{ fontSize: 18, color: 'warning.main' }} />
        <Typography variant="subtitle2" fontWeight="bold">Loss Patterns & Exit Reasons</Typography>
      </Box>

      <Grid container spacing={1} sx={{ mb: 1.5 }}>
        <Grid item xs={6} sm={3}>
          <Box sx={{ p: 1, bgcolor: alpha(theme.palette.error.main, 0.04), borderRadius: 1, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700} color="error.main">{lp.feeVictims || 0}</Typography>
            <Typography variant="caption" color="text.secondary">Fee Victims</Typography>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.6rem' }}>
              gross+ but net-
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ p: 1, bgcolor: alpha(theme.palette.warning.main, 0.04), borderRadius: 1, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700} color="warning.main">{lp.fastLosers || 0}</Typography>
            <Typography variant="caption" color="text.secondary">Fast Losers</Typography>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.6rem' }}>
              closed &le;5min
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ p: 1, bgcolor: alpha(theme.palette.info.main, 0.04), borderRadius: 1, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700} color="info.main">
              {(lp.giveback || 0).toFixed(2)}%
            </Typography>
            <Typography variant="caption" color="text.secondary">Avg Giveback</Typography>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.6rem' }}>
              peak-to-exit
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ p: 1, bgcolor: alpha(theme.palette.secondary.main, 0.04), borderRadius: 1, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700} color="secondary.main">{lp.preProfitLossCapHits || 0}</Typography>
            <Typography variant="caption" color="text.secondary">Loss Cap Hits</Typography>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.6rem' }}>
              net loss cap
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {exitEntries.length > 0 && (
        <>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Exit Reason Distribution ({totalTrades} trades)
          </Typography>
          {exitEntries.slice(0, 8).map(([reason, count], i) => {
            const pct = ((count / totalTrades) * 100).toFixed(0);
            const isPositive = reason.toLowerCase().includes('profit') || reason.toLowerCase().includes('tp');
            return (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.3 }}>
                <Typography variant="caption" sx={{ minWidth: 160, fontWeight: 500 }} noWrap>{reason}</Typography>
                <Box sx={{ flex: 1, height: 6, bgcolor: alpha(isPositive ? theme.palette.success.main : theme.palette.error.main, 0.08), borderRadius: 3 }}>
                  <Box sx={{ width: `${(count / totalTrades) * 100}%`, height: '100%', bgcolor: isPositive ? 'success.main' : 'error.main', borderRadius: 3 }} />
                </Box>
                <Typography variant="caption" fontWeight={600} sx={{ minWidth: 40, textAlign: 'right' }}>{count} ({pct}%)</Typography>
              </Box>
            );
          })}
        </>
      )}
    </Paper>
  );
}

// ============================================================================
// NODE COMPARISON PANEL — §8.1 requirement (cross-strategy comparison)
// ============================================================================
function NodeComparisonPanel({ allNodes }) {
  const theme = useTheme();
  if (!allNodes || allNodes.length <= 1) return null;

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <CompareArrowsIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        <Typography variant="subtitle2" fontWeight="bold">Node Comparison</Typography>
        <Chip label={`${allNodes.length} nodes`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ display: 'flex', py: 0.5, borderBottom: '2px solid', borderColor: 'divider', minWidth: 500 }}>
          {['Strategy', 'Combo #', 'Workdays', 'Trades', 'Holdout', 'Auto', 'Active'].map(h => (
            <Typography key={h} variant="caption" fontWeight={700} sx={{ flex: h === 'Strategy' ? 2 : 1, textAlign: h === 'Strategy' ? 'left' : 'center' }}>{h}</Typography>
          ))}
        </Box>
        {allNodes.map((node, i) => (
          <Box key={i} sx={{ display: 'flex', py: 0.5, borderBottom: '1px solid', borderColor: alpha(theme.palette.divider, 0.3), minWidth: 500, '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) } }}>
            <Typography variant="caption" sx={{ flex: 2, fontWeight: 500 }} noWrap>{node.strategyId?.substring(0, 12)}...</Typography>
            <Typography variant="caption" sx={{ flex: 1, textAlign: 'center' }}>#{node.combinationIndex}</Typography>
            <Typography variant="caption" sx={{ flex: 1, textAlign: 'center' }}>{node.combinationWorkdays}</Typography>
            <Typography variant="caption" sx={{ flex: 1, textAlign: 'center' }}>{node.combinationTrades}</Typography>
            <Typography variant="caption" sx={{ flex: 1, textAlign: 'center' }}>
              <Chip label={node.isHoldout ? 'Yes' : 'No'} size="small" color={node.isHoldout ? 'warning' : 'default'} variant="outlined" sx={{ fontSize: '0.55rem', height: 14 }} />
            </Typography>
            <Typography variant="caption" sx={{ flex: 1, textAlign: 'center' }}>
              <Chip label={node.autoApply ? 'ON' : 'OFF'} size="small" color={node.autoApply ? 'success' : 'default'} variant="outlined" sx={{ fontSize: '0.55rem', height: 14 }} />
            </Typography>
            <Typography variant="caption" sx={{ flex: 1, textAlign: 'center' }}>
              <Chip label={node.enabled ? 'Yes' : 'No'} size="small" color={node.enabled ? 'success' : 'error'} variant="outlined" sx={{ fontSize: '0.55rem', height: 14 }} />
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

// ============================================================================
// SNAPSHOT COMPARISON — Compare 24h/7d/30d metrics
// ============================================================================
function SnapshotComparison({ snapshots }) {
  const theme = useTheme();
  if (!snapshots || snapshots.length === 0) return null;

  // Group by period
  const byPeriod = { '24h': [], '7d': [], '30d': [] };
  snapshots.forEach(s => {
    if (byPeriod[s.period]) byPeriod[s.period].push(s);
  });

  const latest = {};
  ['24h', '7d', '30d'].forEach(p => {
    const sorted = (byPeriod[p] || []).sort((a, b) => new Date(b.generatedAt) - new Date(a.generatedAt));
    latest[p] = sorted[0] || null;
  });

  const metricRow = (label, getter, format, goodIfPositive = true) => (
    <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5, borderBottom: '1px solid', borderColor: alpha(theme.palette.divider, 0.5) }}>
      <Typography variant="caption" sx={{ width: 120, fontWeight: 600 }}>{label}</Typography>
      {['24h', '7d', '30d'].map(p => {
        const val = latest[p] ? getter(latest[p]) : null;
        const formatted = val !== null ? format(val) : '—';
        const color = val !== null
          ? (goodIfPositive ? (val > 0 ? 'success.main' : val < 0 ? 'error.main' : 'text.secondary') : (val < 0 ? 'success.main' : val > 0 ? 'error.main' : 'text.secondary'))
          : 'text.disabled';
        return (
          <Typography key={p} variant="caption" sx={{ flex: 1, textAlign: 'center' }} color={color}>
            {formatted}
          </Typography>
        );
      })}
    </Box>
  );

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <TrendingUpIcon sx={{ fontSize: 18, color: 'success.main' }} />
        <Typography variant="subtitle2" fontWeight="bold">Snapshot Comparison</Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5, borderBottom: '2px solid', borderColor: 'divider' }}>
        <Typography variant="caption" sx={{ width: 120, fontWeight: 700 }}>Metric</Typography>
        {['24h', '7d', '30d'].map(p => (
          <Typography key={p} variant="caption" sx={{ flex: 1, textAlign: 'center', fontWeight: 700 }}>{p}</Typography>
        ))}
      </Box>

      {metricRow('Net PnL', s => s.netPnl, v => `${v > 0 ? '+' : ''}${v.toFixed(2)}%`)}
      {metricRow('Win Rate', s => s.winRate, v => `${(v * 100).toFixed(1)}%`)}
      {metricRow('Profit Factor', s => s.profitFactor, v => v.toFixed(2))}
      {metricRow('Max DD', s => -s.maxDrawdown, v => `${v.toFixed(2)}%`, false)}
      {metricRow('Fee Drag', s => -s.feeDrag, v => `${v.toFixed(2)}%`, false)}
      {metricRow('Trades', s => s.tradeCount, v => String(v))}
      {metricRow('Avg Duration', s => s.avgDurationMinutes, v => `${v.toFixed(0)}m`)}
    </Paper>
  );
}

// ============================================================================
// MAIN EXPERIENCE TAB
// ============================================================================
export default function ExperienceTab({ strategyId, scheduleData }) {
  const theme = useTheme();
  const [status, setStatus] = useState(null);
  const [bestMode, setBestMode] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [windows, setWindows] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [suggestion, setSuggestion] = useState(null);
  const [paramSpace, setParamSpace] = useState(null);
  const [dataStats, setDataStats] = useState(null);
  const [allNodes, setAllNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [applying, setApplying] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    if (!strategyId) return;
    setLoading(true);
    setError(null);
    try {
      const h = headers();
      const [statusRes, bestModeRes, snapshotsRes, windowsRes, ledgerRes, paramSpaceRes, dataStatsRes, allNodesRes] = await Promise.all([
        fetch(`${STRATEGY_ENGINE_URL}/api/admin/experience/${strategyId}/status`, { headers: h }).then(r => r.json()).catch(() => null),
        fetch(`${STRATEGY_ENGINE_URL}/api/admin/experience/${strategyId}/best-mode`, { headers: h }).then(r => r.json()).catch(() => null),
        fetch(`${STRATEGY_ENGINE_URL}/api/admin/experience/${strategyId}/snapshots?days=30`, { headers: h }).then(r => r.json()).catch(() => null),
        fetch(`${STRATEGY_ENGINE_URL}/api/admin/experience/${strategyId}/windows`, { headers: h }).then(r => r.json()).catch(() => null),
        fetch(`${STRATEGY_ENGINE_URL}/api/admin/experience/${strategyId}/ledger`, { headers: h }).then(r => r.json()).catch(() => null),
        fetch(`${STRATEGY_ENGINE_URL}/api/admin/experience/parameter-space/${strategyId}`, { headers: h }).then(r => r.json()).catch(() => null),
        fetch(`${STRATEGY_ENGINE_URL}/api/admin/experience/data-stats/${strategyId}`, { headers: h }).then(r => r.json()).catch(() => null),
        fetch(`${STRATEGY_ENGINE_URL}/api/admin/experience/all-nodes`, { headers: h }).then(r => r.json()).catch(() => null),
      ]);
      if (statusRes?.success) setStatus(statusRes);
      if (bestModeRes?.success) setBestMode(bestModeRes.data);
      if (snapshotsRes?.success) setSnapshots(snapshotsRes.data || []);
      if (windowsRes?.success) setWindows(windowsRes.data);
      if (ledgerRes?.success) setLedger(ledgerRes.data || []);
      if (paramSpaceRes?.success) setParamSpace(paramSpaceRes);
      if (dataStatsRes?.success) setDataStats(dataStatsRes);
      if (allNodesRes?.success) setAllNodes(allNodesRes.data || []); // Fetch allNodes
    } catch (err) {
      setError('Failed to load experience data');
    } finally {
      setLoading(false);
    }
  }, [strategyId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${STRATEGY_ENGINE_URL}/api/admin/experience/${strategyId}/suggest`, {
        method: 'POST', headers: headers(),
      });
      const data = await res.json();
      if (data.success) setSuggestion(data.data);
      else setError(data.error || 'Failed to generate suggestion');
    } catch (err) {
      setError('Failed to generate suggestion');
    } finally {
      setGenerating(false);
    }
  };

  const handleApply = async (patch) => {
    setApplying(true);
    try {
      const res = await fetch(`${STRATEGY_ENGINE_URL}/api/admin/experience/${strategyId}/apply`, {
        method: 'POST', headers: headers(), body: JSON.stringify({ patch }),
      });
      const data = await res.json();
      if (data.success) {
        setSuggestion(null);
        await loadData();
      } else {
        setError(data.error || 'Failed to apply patch');
      }
    } catch (err) {
      setError('Failed to apply patch');
    } finally {
      setApplying(false);
    }
  };

  const handleInitParams = async () => {
    setInitializing(true);
    try {
      const res = await fetch(`${STRATEGY_ENGINE_URL}/api/admin/experience/init-parameter-space/${strategyId}`, {
        method: 'POST', headers: headers(),
      });
      const data = await res.json();
      if (data.success) await loadData();
      else setError(data.error || 'Failed to initialize parameters');
    } catch (err) {
      setError('Failed to initialize parameter space');
    } finally {
      setInitializing(false);
    }
  };

  const handleAdvance = async () => {
    setAdvancing(true);
    try {
      const res = await fetch(`${STRATEGY_ENGINE_URL}/api/admin/experience/${strategyId}/advance`, {
        method: 'POST', headers: headers(),
      });
      const data = await res.json();
      if (data.success) await loadData();
      else setError(data.error || 'Failed to advance');
    } catch (err) {
      setError('Failed to advance combination');
    } finally {
      setAdvancing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  const isEnabled = status?.data?.experienceModeEnabled;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Tooltip title="Refresh experience data">
          <IconButton size="small" onClick={loadData}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Grid container spacing={2}>
        {/* Row 1: Status */}
        <Grid item xs={12}>
          <StatusCard status={status} onAdvance={handleAdvance} advancing={advancing} />
        </Grid>

        {isEnabled && (
          <>
            {/* Row 2: Node Comparison (cross-strategy) */}
            {allNodes.length > 1 && (
              <Grid item xs={12}>
                <NodeComparisonPanel allNodes={allNodes} />
              </Grid>
            )}

            {/* Row 3: Best Mode + Snapshots */}
            <Grid item xs={12} md={7}>
              <BestModePanel bestMode={bestMode} />
            </Grid>
            <Grid item xs={12} md={5}>
              <SnapshotComparison snapshots={snapshots} />
            </Grid>

            {/* Row 4: Funnel Blockers + Loss Patterns */}
            <Grid item xs={12} md={6}>
              <FunnelBlockersPanel snapshots={snapshots} />
            </Grid>
            <Grid item xs={12} md={6}>
              <LossPatternsPanel snapshots={snapshots} />
            </Grid>

            {/* Row 5: Suggestion + Windows */}
            <Grid item xs={12} md={7}>
              <SuggestionPanel
                strategyId={strategyId}
                suggestion={suggestion}
                onGenerate={handleGenerate}
                onApply={handleApply}
                generating={generating}
                applying={applying}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <WindowsPanel windows={windows} />
            </Grid>

            {/* Row 6: Parameter Space + Data Collection */}
            <Grid item xs={12} md={6}>
              <ParameterSpacePanel paramSpace={paramSpace} onInit={handleInitParams} initializing={initializing} />
            </Grid>
            <Grid item xs={12} md={6}>
              <DataCollectionPanel dataStats={dataStats} />
            </Grid>

            {/* Row 7: Experiment Timeline (Ledger) */}
            <Grid item xs={12}>
              <LedgerPanel ledger={ledger} />
            </Grid>
          </>
        )}

        {!isEnabled && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 4, border: '1px dashed', borderColor: 'divider', borderRadius: 2, textAlign: 'center' }}>
              <ScienceIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body1" fontWeight={600} color="text.secondary" gutterBottom>
                Experience Mode is Off
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto' }}>
                Enable Experience Mode in the Settings tab to start round-robin experimentation
                across parameter combinations. The system will collect performance data, score each
                combination, and recommend the best settings for your market regime.
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
